import { noteNameSchema, type Melody, type NoteName, type Scale } from '@tunasong/schemas'
import { isInKey, spellInKey } from '../../chord/concert-pitch.js'
import { barBeats, beatMs, feltBeat } from '../grid.js'

/**
 * Cuts a melody into phrases a singer can hold in their head, by Bowie's rules for Pitch Trainer
 * Phase 2 (plan `docs/superpowers/plans/2026-10-19-pitch-trainer-phase2-plan.md`, M1):
 *
 * - a phrase ends at a rest of a beat or more, and after a note held two beats or more;
 * - consecutive notes on the same semitone are one pitch event, because a pitch tracker hears one;
 * - a phrase of more than 10 events is cut at a bar line (4-bar, then 2-bar, then any), with a pickup
 *   in the last beat before the line going to the phrase after it;
 * - a phrase lasting more than 8 s at its playback rate is cut the same way, if 4 events stay each side;
 * - a phrase of fewer than 4 events joins a neighbour, as long as that leaves 12 or fewer.
 *
 * Nothing is dropped: notes too short to sing apart slow the phrase down instead.
 */

export type PhraseLevel = 'easy' | 'medium' | 'hard'

export type PhraseReason =
  | { kind: 'leap'; semitones: number }
  | { kind: 'chromatic'; count: number }
  | { kind: 'span'; semitones: number }
  | { kind: 'fast' }

/** Where a melody sits in the song, as the caller knows it */
export interface PhraseSource {
  sectionName: string
  /** The suggested phrase comes from a chorus when there is one */
  isChorus?: boolean
  /** 1-based, set only when a section has two or more melodies ("melody 2") */
  melodyIndex?: number
}

export interface PhraseOptions extends PhraseSource {
  /** The section's or song's key, for a melody without a key of its own */
  key?: Scale
}

export interface PhraseOrigin extends PhraseSource {
  /** 1-based, counted from the start of the melody */
  startBar: number
  endBar: number
}

/** One pitch to sing: a note, or a run of notes on the same semitone */
export interface PhraseEvent {
  midi: number
  /** Beats from the start of the melody: the first source note's onset */
  onset: number
  /** Beats, from the first source note's onset to the last one's end */
  duration: number
  /** Indices into `melody.notes` */
  noteIndices: number[]
  /** Spelled in the melody's key */
  name: NoteName
  /** 1-based bar of the first source note */
  bar: number
}

export interface Phrase {
  events: PhraseEvent[]
  source: PhraseOrigin
  level: PhraseLevel
  /** Why the phrase is above easy; empty when it is easy */
  reasons: PhraseReason[]
  /** Playback rate, 0.67–1 */
  rate: number
  /** Other places the same phrase occurs, filled by `dedupePhrases` */
  alsoIn: PhraseOrigin[]
  /** The phrase's source notes as a melody of their own, from the downbeat of its first bar */
  slice: Melody
}

const cutRestBeats = 1
const cutHeldBeats = 2
const maxEvents = 10
const maxPhraseMs = 8000
const minEvents = 4
const maxMergedEvents = 12
/**
 * Bowie, M6a: a pickup is a lead-in set apart from what comes before it. Counted in felt beats: a lead-in is
 * what starts in the last beat before a bar line, set apart by a rest of at least a 16th's share of the beat,
 * or by a note more than twice its longest note. A shorter rest inside the 2 beats before the line makes
 * everything after it the lead-in.
 */
const pickupBeats = 1
const pickupRestBeats = 2
const pickupRestShare = 0.25
const pickupHeldFactor = 2
const minEventMs = 200
const minRate = 0.67
const barEpsilon = 1e-6

const sharpNames: NoteName[] = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B']

const levels: PhraseLevel[] = ['easy', 'medium', 'hard']
const levelRank = (level: PhraseLevel) => levels.indexOf(level)
const worst = (a: PhraseLevel, b: PhraseLevel) => (levelRank(a) >= levelRank(b) ? a : b)

const pitchClass = (midi: number) => ((midi % 12) + 12) % 12

type Note = Melody['notes'][number]
type Segment = number[]
/** A pitch event as merged, before a phrase names it and gives it a bar */
type PitchEvent = Omit<PhraseEvent, 'name' | 'bar'>

/** Note indices in onset order */
const onsetOrder = (notes: Note[]) => notes.map((_note, index) => index).sort((a, b) => notes[a].onset - notes[b].onset)

/** Split note indices (in onset order) at rests and held notes */
const cutAtRests = (notes: Note[], order: number[]): Segment[] =>
  order.reduce<Segment[]>((segments, index, position) => {
    const previousIndex = order[position - 1]
    const current = segments[segments.length - 1]
    if (previousIndex === undefined || current === undefined) {
      return [[index]]
    }
    const previous = notes[previousIndex]
    const rest = notes[index].onset - (previous.onset + previous.duration)
    const isCut = rest >= cutRestBeats || previous.duration >= cutHeldBeats
    return isCut ? [...segments, [index]] : [...segments.slice(0, -1), [...current, index]]
  }, [])

/** Merge runs on the same semitone; spelling comes later, once phrase boundaries are final */
const toEvents = (notes: Note[], segment: Segment) =>
  segment.reduce<PitchEvent[]>((events, index) => {
    const note = notes[index]
    const last = events[events.length - 1]
    if (last && last.midi === note.midi) {
      const end = note.onset + note.duration
      return [
        ...events.slice(0, -1),
        { ...last, duration: end - last.onset, noteIndices: [...last.noteIndices, index] },
      ]
    }
    return [...events, { midi: note.midi, onset: note.onset, duration: note.duration, noteIndices: [index] }]
  }, [])

const rateFor = (events: PitchEvent[], bpm: number) => {
  const shortestMs = Math.min(...events.map(event => event.duration * beatMs(bpm)))
  const rate = Math.min(1, shortestMs / minEventMs)
  return { rate: Math.max(minRate, rate), isFast: rate < minRate }
}

/** How long the phrase lasts at its playback rate, from its first onset to its last note's end */
const phraseMs = (events: PitchEvent[], bpm: number) => {
  const first = events[0]
  const last = events[events.length - 1]
  return ((last.onset + last.duration - first.onset) * beatMs(bpm)) / rateFor(events, bpm).rate
}

/** The gap of silence before event `at`, in beats */
const restBefore = (events: PitchEvent[], at: number) => {
  const previous = events[at - 1]
  return previous ? events[at].onset - (previous.onset + previous.duration) : 0
}

/**
 * The first event of the half after bar line `line` (Bowie, M6a): the events on or after it, plus a pickup
 * when one is set apart. Without one, the cut is exactly on the line.
 */
const cutAtLine = (events: PitchEvent[], line: number, beat: number) => {
  const onLine = events.findIndex(event => event.onset >= line - barEpsilon)
  /** A rest inside the 2 beats before the line: everything after the latest such rest leads in */
  for (let at = onLine - 1; at > 0 && events[at].onset >= line - pickupRestBeats * beat - barEpsilon; at--) {
    const rest = restBefore(events, at)
    if (rest >= pickupRestShare * beat && rest < cutRestBeats) {
      return at
    }
  }
  /** Otherwise only what starts in the last beat, set apart by a longer note before it */
  const first = events.findIndex(event => event.onset >= line - pickupBeats * beat - barEpsilon)
  const previous = events[first - 1]
  if (first === onLine || !previous) {
    return onLine
  }
  const longest = Math.max(...events.slice(first, onLine).map(event => event.duration))
  return previous.duration > pickupHeldFactor * longest ? first : onLine
}

/**
 * Where to cut an over-long segment: the bar line with the most even halves, preferring 4-bar lines,
 * then 2-bar, then any bar. A pickup set apart before the line goes after it (`cutAtLine`).
 * A line counts only if it leaves 4 or more events each side; undefined when none does.
 */
const splitPoint = (events: PitchEvent[], perBar: number, beat: number, minSide = minEvents): number | undefined => {
  const first = events[0].onset
  const last = events[events.length - 1].onset
  for (const bars of [4, 2, 1]) {
    const step = bars * perBar
    const cuts: number[] = []
    for (let line = (Math.floor(first / step) + 1) * step; line <= last; line += step) {
      const cut = cutAtLine(events, line, beat)
      const leavesTwoPhrases = cut >= minSide && events.length - cut >= minSide
      if (leavesTwoPhrases && !cuts.includes(cut)) {
        cuts.push(cut)
      }
    }
    if (cuts.length > 0) {
      const balance = (cut: number) => Math.abs(events.length - 2 * cut)
      return cuts.reduce((best, cut) => (balance(cut) < balance(best) ? cut : best))
    }
  }
  return undefined
}

/**
 * Over 10 events, a segment is always cut: at a bar line, else at its middle event. Over 8 s at its
 * playback rate it is cut only at a bar line that leaves 4 events each side; time alone never makes a fragment.
 */
const capLength = (notes: Note[], segment: Segment, perBar: number, beat: number, bpm: number): Segment[] => {
  const events = toEvents(notes, segment)
  const isTooMany = events.length > maxEvents
  if (!isTooMany && phraseMs(events, bpm) <= maxPhraseMs) {
    return [segment]
  }
  const cut = splitPoint(events, perBar, beat) ?? (isTooMany ? Math.floor(events.length / 2) : undefined)
  if (cut === undefined) {
    return [segment]
  }
  const at = segment.indexOf(events[cut].noteIndices[0])
  return [
    ...capLength(notes, segment.slice(0, at), perBar, beat, bpm),
    ...capLength(notes, segment.slice(at), perBar, beat, bpm),
  ]
}

const eventCount = (notes: Note[], segment: Segment) => toEvents(notes, segment).length

/** Two consecutive segments joined stay inside the 12-event and 8 s limits (the 8 s: Bowie, #2418 approval) */
const canJoin = (notes: Note[], first: Segment, second: Segment, bpm: number) => {
  const events = toEvents(notes, [...first, ...second])
  return events.length <= maxMergedEvents && phraseMs(events, bpm) <= maxPhraseMs
}

/** A fragment of fewer than 4 events joins the next segment, or else the previous, when the result fits */
const joinShort = (notes: Note[], segments: Segment[], bpm: number): Segment[] => {
  const fitsNext = (position: number) => {
    const next = segments[position + 1]
    return next !== undefined && canJoin(notes, segments[position], next, bpm)
  }
  const fitsPrevious = (position: number) => {
    const previous = segments[position - 1]
    return previous !== undefined && canJoin(notes, previous, segments[position], bpm)
  }
  const shortAt = segments.findIndex(
    (segment, position) => eventCount(notes, segment) < minEvents && (fitsNext(position) || fitsPrevious(position))
  )
  if (shortAt === -1) {
    return segments
  }
  const segment = segments[shortAt]
  const joined = fitsNext(shortAt)
    ? [...segments.slice(0, shortAt), [...segment, ...segments[shortAt + 1]], ...segments.slice(shortAt + 2)]
    : [...segments.slice(0, shortAt - 1), [...segments[shortAt - 1], ...segment], ...segments.slice(shortAt + 1)]
  return joinShort(notes, joined, bpm)
}

function isNoteName(name: string): name is NoteName {
  return noteNameSchema.safeParse(name).success
}

/** One step up: Eb → E, B → B#; undefined rather than a double sharp */
const raise = (name: string) => {
  if (name.endsWith('#')) {
    return undefined
  }
  return name.endsWith('b') ? name.slice(0, -1) : `${name}#`
}

/** One step down: F# → F, F → Fb; undefined rather than a double flat */
const lower = (name: string) => {
  if (name.endsWith('b')) {
    return undefined
  }
  return name.endsWith('#') ? name.slice(0, -1) : `${name}b`
}

/**
 * A chromatic note is spelled from the key's neighbouring letter, by the way it goes: rising, the in-key
 * note a semitone below raised; falling, the in-key note a semitone above lowered. When that would take a
 * double accidental, the other neighbour's form is used.
 */
const spellChromatic = (pc: number, isRising: boolean, key: Scale): NoteName => {
  const neighbour = (offset: number) => {
    const neighbourPc = pitchClass(pc + offset)
    return isInKey(sharpNames[neighbourPc], key) ? spellInKey(neighbourPc, key) : undefined
  }
  const below = neighbour(-1)
  const above = neighbour(1)
  const fromBelow = below === undefined ? undefined : raise(below)
  const fromAbove = above === undefined ? undefined : lower(above)
  const name = isRising ? (fromBelow ?? fromAbove) : (fromAbove ?? fromBelow)
  return name !== undefined && isNoteName(name) ? name : spellInKey(pc, key)
}

/**
 * In the key: the key's own spelling. Outside it, by where the note resolves: the note after it, or for a
 * phrase's last note the one before it; a note alone takes the key's accidental. No key: sharps.
 * (Bowie's #2418 review.)
 */
const spell = (midi: number, previous: number | undefined, next: number | undefined, key: Scale | undefined) => {
  const pc = pitchClass(midi)
  if (!key) {
    return sharpNames[pc]
  }
  if (isInKey(sharpNames[pc], key) || (previous === undefined && next === undefined)) {
    return spellInKey(pc, key)
  }
  const isRising = next === undefined ? midi > (previous ?? midi) : next > midi
  return spellChromatic(pc, isRising, key)
}

const leapLevel = (semitones: number): PhraseLevel => {
  if (semitones <= 5) {
    return 'easy'
  }
  return semitones <= 12 ? 'medium' : 'hard'
}

const chromaticLevel = (count: number): PhraseLevel => {
  if (count === 0) {
    return 'easy'
  }
  return count <= 2 ? 'medium' : 'hard'
}

const spanLevel = (semitones: number): PhraseLevel => {
  if (semitones <= 14) {
    return 'easy'
  }
  return semitones <= 16 ? 'medium' : 'hard'
}

const judge = (midis: number[], key: Scale | undefined, isFast: boolean) => {
  const leap = Math.max(0, ...midis.slice(1).map((midi, index) => Math.abs(midi - midis[index])))
  const span = Math.max(...midis) - Math.min(...midis)
  const chromatic = key ? midis.filter(midi => !isInKey(sharpNames[pitchClass(midi)], key)).length : 0
  const judged: [PhraseLevel, PhraseReason][] = [
    [leapLevel(leap), { kind: 'leap', semitones: leap }],
    [chromaticLevel(chromatic), { kind: 'chromatic', count: chromatic }],
    [spanLevel(span), { kind: 'span', semitones: span }],
    [isFast ? 'hard' : 'easy', { kind: 'fast' }],
  ]
  const above = judged.filter(([level]) => level !== 'easy')
  return {
    level: above.reduce<PhraseLevel>((level, [next]) => worst(level, next), 'easy'),
    reasons: above.map(([, reason]) => reason),
  }
}

/** One phrase from note indices in onset order: its events spelled in `key`, bars, slice, difficulty and rate */
const buildPhrase = (melody: Melody, segment: Segment, source: PhraseSource, key: Scale | undefined): Phrase => {
  const { notes } = melody
  const perBar = barBeats(melody.tempo.meter)
  const barOf = (onset: number) => Math.floor(onset / perBar) + 1
  const raw = toEvents(notes, segment)
  const events = raw.map((event, index) => ({
    ...event,
    name: spell(event.midi, raw[index - 1]?.midi, raw[index + 1]?.midi, key),
    bar: barOf(event.onset),
  }))
  const first = events[0]
  const last = events[events.length - 1]
  const startBar = barOf(first.onset)
  const sliceStart = (startBar - 1) * perBar
  const { rate: playbackRate, isFast } = rateFor(events, melody.tempo.bpm)
  return {
    events,
    source: { ...source, startBar, endBar: barOf(last.onset + last.duration - barEpsilon) },
    slice: { ...melody, notes: segment.map(index => ({ ...notes[index], onset: notes[index].onset - sliceStart })) },
    ...judge(
      events.map(event => event.midi),
      key,
      isFast
    ),
    rate: playbackRate,
    alsoIn: [],
  }
}

export const extractPhrases = (melody: Melody, options: PhraseOptions): Phrase[] => {
  const { key: fallbackKey, ...source } = options
  const { notes } = melody
  const key = melody.key ?? fallbackKey
  const perBar = barBeats(melody.tempo.meter)
  const order = onsetOrder(notes)
  const beat = feltBeat(melody.tempo.meter)
  const capped = cutAtRests(notes, order).flatMap(segment => capLength(notes, segment, perBar, beat, melody.tempo.bpm))
  return joinShort(notes, capped, melody.tempo.bpm).map(segment => buildPhrase(melody, segment, source, key))
}

/** The fewest events a half may have, so a 7-event phrase splits 3 + 4 (Joni §8) */
const minHalfEvents = 3

/**
 * A phrase's two halves, for the refusal screen and the too-long control (plan M3 step 3.3): cut at the
 * strongest bar line inside it (4-bar, then 2-bar, then any), with pickups going to the second half and
 * never mid-note; at the middle event when no bar line leaves 3 events each side. Undefined for a phrase
 * of fewer than 6 events.
 */
export const splitPhrase = (
  melody: Melody,
  phrase: Phrase,
  options: Pick<PhraseOptions, 'key'> = {}
): [Phrase, Phrase] | undefined => {
  const segment = phrase.events.flatMap(event => event.noteIndices)
  const raw = toEvents(melody.notes, segment)
  if (raw.length < 2 * minHalfEvents) {
    return undefined
  }
  const { meter } = melody.tempo
  const cut = splitPoint(raw, barBeats(meter), feltBeat(meter), minHalfEvents) ?? Math.floor(raw.length / 2)
  const at = segment.indexOf(raw[cut].noteIndices[0])
  const { startBar: _startBar, endBar: _endBar, ...source } = phrase.source
  const key = melody.key ?? options.key
  return [buildPhrase(melody, segment.slice(0, at), source, key), buildPhrase(melody, segment.slice(at), source, key)]
}

const signature = (phrase: Phrase) => phrase.events.map(event => `${event.midi}:${event.duration}`).join(' ')

/** A phrase repeated note for note and beat for beat is listed once, with the other places it occurs */
export const dedupePhrases = (phrases: Phrase[]): Phrase[] => {
  const firsts = new Map<string, Phrase>()
  for (const phrase of phrases) {
    const key = signature(phrase)
    const first = firsts.get(key)
    firsts.set(key, first ? { ...first, alsoIn: [...first.alsoIn, phrase.source] } : phrase)
  }
  return [...firsts.values()]
}

const easiest = (phrases: Phrase[]) =>
  phrases.reduce<Phrase | undefined>(
    (best, phrase) => (best && levelRank(best.level) <= levelRank(phrase.level) ? best : phrase),
    undefined
  )

/** The phrase to offer first: the easiest in a chorus, or else the easiest in song order */
export const suggestPhrase = (phrases: Phrase[]): Phrase | undefined =>
  easiest(phrases.filter(phrase => phrase.source.isChorus)) ?? easiest(phrases)
